/* eslint-disable no-plusplus */
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  makeStyles,
  Tooltip,
  IconButton,
  withStyles,
} from "@material-ui/core";
import { useEffectAsync } from "lib/react/customHooks";
import mixpanelTrack from "app/common/MixpanelComponent";
import { FiMoreVertical } from "react-icons/fi";
import { primaryColor } from "resources/constants";
import getDemand from "lib/requests/demand";
import getHistorics from "lib/requests/historics";
import moment from "moment";
import ScaleLoader from "react-spinners/ScaleLoader";
import CardComponent from "app/common/CardComponent";
import ExportElasticitiesCard from "../elasticity/export_excel/ExportElasticitiesCard";
import DemandPlanningCardComponent from "./DemandPlanningCardComponent";

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.up("lg")]: {
      overflow: "hidden",
      flexGrow: "1",
    },
  },
  headerContainer: {
    display: "flex",
    borderBottom: "1px solid #e2e2e2",
    marginBottom: "10px",
    marginLeft: "10px",
    marginRight: "10px",
    flexWrap: "nowrap",
    [theme.breakpoints.down("xs")]: {
      flexWrap: "wrap",
      paddingBottom: "10px",
    },
  },
  headerTitle: {
    paddingBottom: theme.spacing(1.5),
  },
  iconExportContainer: {
    display: "flex",
    paddingBottom: "10px",
  },
  spinnerContainer: {
    display: "flex",
    flexGrow: "1",
    alignItems: "center",
    justifyContent: "center",
  },
  infoForNoCustomers: {
    margin: "auto",
  },
  infoForNoCustomersTitle: {
    fontSize: "36px",
  },
  infoForNoCustomersBody: {
    fontSize: "20px",
    display: "flex",
    justifyContent: "center",
  },
  infoForNoCustomersContainer: {
    color: "white",
    display: "flex",
    justifyContent: "center",
    padding: "10px 20px",
  },
  infoForNoCustomersButton: {
    fontSize: "26px",
    backgroundColor: theme.palette.primary.main,
    margin: "auto",
    marginTop: "30px",
    border: "1px solid theme.palette.primary.main",
    borderRadius: "15px",
    textDecoration: "none",
    width: "20%",
    display: "flex",
    justifyContent: "center",
  },
  halfHeight: theme.halfHeight,
  explicabilityContainer: {},
}));

const StyledIconButton = withStyles(() => ({
  root: {
    padding: "0px",
    flex: 1,
    direction: "rtl",
    flexDirection: "row",
  },
}))(IconButton);

const StyledTypography = withStyles(() => ({
  root: {
    flex: 1,
    direction: "ltr",
    flexDirection: "row-reverse",
  },
}))(Typography);

function DemandPlanningComponent() {
  const classes = useStyles();
  const demandPlanningExport = true;
  const configCatSlice = useSelector((state) => state.auth.configCatUser);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [demandPlanningEstimations, setDemandPlanningEstimations] = useState(
    []
  );
  const [historicsData, setHistoricsData] = useState();
  const [minDateExportDemand, setMinDateExportDemand] = useState(null);
  const [maxDateExportDemand, setMaxDateExportDemand] = useState(null);
  const [demandCount, setDemandCount] = useState(0);
  const [bandera, setBandera] = useState(false);

  const [excelGroupBy, setExcelGroupBy] = useState({
    key: "product",
    label: "By Sku",
    field: "Sku",
    tooltip: "Sku",
  });
  const [excelGroupByTwo, setExcelGroupByTwo] = useState({
    key: null,
    label: "-",
  });
  const [checkboxChecked, setCheckboxChecked] = useState(true);

  const handleExportExcelClick = () => {
    /* MIXPANEL TRACKS */
    mixpanelTrack(`Open dialog export demand predictions`);
    /* END OF MIXPANEL TRACKS */
    setExcelGroupBy({
      key: "product",
      label: "By Product",
      field: "Product",
      tooltip: "Product",
    });
    setExcelGroupByTwo({
      key: null,
      label: "-",
    });
    setCheckboxChecked(true);
    setConfirmDialogOpen(true);
  };

  const handleDialogClose = () => {
    setExcelGroupBy({
      key: "product",
      label: "By Product",
      field: "Product",
      tooltip: "Product",
    });
    setExcelGroupByTwo({
      key: null,
      label: "-",
    });
    setCheckboxChecked(true);
    setConfirmDialogOpen(false);
  };

  const demandDataBeforeHistoricData = () => {
    return configCatSlice?.features.isDemandPlanning ? (
      <DemandPlanningCardComponent
        demandPlanningEstimations={demandPlanningEstimations}
        historicsData={historicsData}
      />
    ) : (
      <CardComponent
        body={
          <div className={classes.infoForNoCustomers}>
            <div className={classes.infoForNoCustomersTitle}>
              Demand planning module is not included in your current plan.
            </div>
            <div className={classes.infoForNoCustomersBody}>
              Upgrade your subscription to access more features
            </div>

            <a
              href="https://www.wiseathena.com/solution/"
              target="_blank"
              rel="noreferrer"
              className={classes.infoForNoCustomersButton}
            >
              {" "}
              <div className={classes.infoForNoCustomersContainer}>Info + </div>
            </a>
          </div>
        }
      />
    );
  };

  useEffectAsync(
    async () => {
      const getMinDate = getDemand({
        group_by: "date",
        order_by: "date",
        limit: 1,
        order_direction: "asc",
      });

      const getMaxDate = getDemand({
        group_by: "date",
        order_by: "date",
        limit: 1,
        order_direction: "desc",
      });

      const responses = await Promise.all([getMinDate, getMaxDate]);
      return responses;
    },
    (responses) => {
      if (responses[0].data[0] !== undefined) {
        setBandera(true);
        setMinDateExportDemand(responses[0].data[0].date);
        setMaxDateExportDemand(responses[1].data[0].date);
      }
    },
    []
  );

  useEffectAsync(
    async () => {
      let responses = [];
      if (minDateExportDemand && maxDateExportDemand) {
        const minDateDemand = moment(maxDateExportDemand)
          .subtract(23, "months")
          .startOf("month")
          .format("YYYY-MM-DD");
        const maxDateDemand = moment(maxDateExportDemand)
          .endOf("month")
          .format("YYYY-MM-DD");
        const demandCounter = getDemand({
          count: "id",
          min_date: minDateDemand,
          max_date: maxDateDemand,
        });

        responses = await Promise.all([demandCounter]);
      }

      return responses;
    },
    (responses) => {
      if (
        minDateExportDemand &&
        maxDateExportDemand &&
        responses[0].data[0] !== undefined
      ) {
        setDemandCount(responses[0].data[0].count_id);
      }
    },
    [minDateExportDemand, maxDateExportDemand]
  );

  useEffectAsync(
    async () => {
      let responses = {};
      let response = {};
      if (minDateExportDemand && maxDateExportDemand) {
        let iterations = 0;
        do {
          let minDateHistorics = null;
          let maxDateHistorics = null;
          if (
            moment(minDateExportDemand) <=
            moment(maxDateExportDemand).subtract(24, "months")
          ) {
            minDateHistorics = moment(maxDateExportDemand)
              .subtract(36, "months")
              .startOf("month")
              .format("YYYY-MM-DD");
            maxDateHistorics = moment(maxDateExportDemand)
              .subtract(24, "months")
              .endOf("month")
              .format("YYYY-MM-DD");
          } else {
            minDateHistorics = moment(minDateExportDemand)
              .startOf("month")
              .format("YYYY-MM-DD");
            maxDateHistorics = moment(maxDateExportDemand)
              .endOf("month")
              .format("YYYY-MM-DD");
          }
          const historics = getHistorics({
            filters: {
              data_source_id: 1,
            },
            order_by: ["date"],
            order_direction: ["asc"],
            min_date: minDateHistorics,
            max_date: maxDateHistorics,
            limit: 20000,
            offset: iterations * 20000,
          });

          iterations += 1;
          // eslint-disable-next-line no-await-in-loop
          response = await Promise.all([historics]);
          responses = { ...responses, ...response[0].data };
        } while (response[0].data.length !== 0);
      }
      return responses;
    },
    (responses) => {
      if (minDateExportDemand && maxDateExportDemand)
        setHistoricsData(responses);
    },
    [minDateExportDemand, maxDateExportDemand]
  );

  useEffectAsync(
    async () => {
      const numberOfRows = 5000;

      const paginations = Math.ceil(demandCount / numberOfRows);
      const demandPaginationQueries = [];

      // Fix double request
      if (bandera) {
        const minDateDemand = moment(maxDateExportDemand)
          .subtract(23, "months")
          .startOf("month")
          .format("YYYY-MM-DD");
        const maxDateDemand = moment(maxDateExportDemand)
          .endOf("month")
          .format("YYYY-MM-DD");
        for (let i = 0; i < paginations; i++) {
          demandPaginationQueries.push(
            getDemand({
              min_date: minDateDemand,
              max_date: maxDateDemand,
              limit: numberOfRows,
              offset: numberOfRows * i,
            })
          );
        }
      }

      const responses = await Promise.all(demandPaginationQueries);
      return responses;
    },
    (result) => {
      const demandPredictionsResponses = [];

      for (let i = 0; i < result.length; i++) {
        demandPredictionsResponses.push(result[i].data);
      }
      const demandDataFlattend = demandPredictionsResponses.flat(1);
      setDemandPlanningEstimations(demandDataFlattend);
    },
    [historicsData]
  );

  useEffect(() => {
    /* MIXPANEL TRACKS */
    mixpanelTrack("Demand Planning module visited");
    /* END OF MIXPANEL TRACKS */
  }, []);

  return (
    <>
      <ExportElasticitiesCard
        open={confirmDialogOpen}
        handleClose={handleDialogClose}
        minDate={minDateExportDemand}
        maxDate={maxDateExportDemand}
        excelGroupBy={excelGroupBy}
        setExcelGroupBy={setExcelGroupBy}
        excelGroupByTwo={excelGroupByTwo}
        setExcelGroupByTwo={setExcelGroupByTwo}
        checkboxChecked={checkboxChecked}
        setCheckboxChecked={setCheckboxChecked}
        isDemandPlanning={demandPlanningExport}
      />
      <Box className={classes.headerContainer}>
        <StyledTypography variant="h6" className={classes.headerTitle}>
          Demand Planning
        </StyledTypography>
        {configCatSlice?.features.isDemandPlanning && (
          <Tooltip title="Export Demand Planning" placement="left">
            <Box className={classes.iconExportContainer}>
              <StyledIconButton
                aria-label="export demand planning"
                component="span"
                disableRipple
                onClick={handleExportExcelClick}
              >
                <FiMoreVertical size={20} />
              </StyledIconButton>
            </Box>
          </Tooltip>
        )}
      </Box>
      {configCatSlice?.features.isDemandPlanning &&
      (demandPlanningEstimations.length === 0 ||
        historicsData === undefined) ? (
        <Box component="div" className={classes.spinnerContainer}>
          <ScaleLoader size={150} color={primaryColor} loading />
        </Box>
      ) : (
        demandDataBeforeHistoricData()
      )}
    </>
  );
}

export default DemandPlanningComponent;
